import { useState, useEffect, useCallback } from 'react'
import { getPage, post, del as delReq } from '../lib/api'

export interface PortfolioSection {
  heading: string
  body: string
  /** All richer fields are optional — the backend normalizer only populates
   *  them when the AI emits them, so the UI must guard for their absence. */
  bullet_points?: string[]
  techStack?: string[]
  outcomes?: string[]
  role?: string
  period?: string
}

export interface PortfolioContent {
  title: string
  summary: string
  sections: PortfolioSection[]
}

export interface Portfolio {
  id: string
  talentId: string
  content: PortfolioContent
  notionPageId: string | null
  pushedAt: string | null
  status: string
  createdAt: string
  /** Backend computes counseling mode at generate-time but does not yet
   *  persist it on the row; kept optional so the badge lights up if added. */
  counselingMode?: boolean
}

export function usePortfolios(talentId: string | undefined) {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)

  const load = useCallback(async () => {
    if (!talentId) {
      setPortfolios([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await getPage<Portfolio>(
        `/api/v1/talents/${talentId}/portfolios`,
      )
      setPortfolios(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류')
    } finally {
      setLoading(false)
    }
  }, [talentId])

  useEffect(() => {
    load()
  }, [load])

  // POST /generate — backend reads sources + resumes from DB; controls optional.
  const generate = useCallback(
    async (
      opts?: {
        packagingLevel?: number
        frameworks?: string[]
        forbiddenWords?: string[]
        requiredKeywords?: string[]
      },
    ) => {
      if (!talentId) return
      setGenerating(true)
      try {
        await post<Portfolio>(
          `/api/v1/talents/${talentId}/portfolios/generate`,
          opts,
        )
        await load()
      } catch (e) {
        throw e instanceof Error ? e : new Error('AI 생성 실패')
      } finally {
        setGenerating(false)
      }
    },
    [talentId, load],
  )

  const pushNotion = useCallback(
    async (id: string) => {
      if (!talentId) return
      await post<{ status: string }>(
        `/api/v1/talents/${talentId}/portfolios/${id}/push-notion`,
      )
      await load()
    },
    [talentId, load],
  )

  const remove = useCallback(
    async (id: string) => {
      if (!talentId) return
      await delReq<{ status: string }>(
        `/api/v1/talents/${talentId}/portfolios/${id}`,
      )
      await load()
    },
    [talentId, load],
  )

  return {
    portfolios,
    loading,
    error,
    reload: load,
    generate,
    generating,
    pushNotion,
    remove,
  }
}
